import React, { useState } from 'react';

import { useDateFormatter } from '@mint-lib/formatting-hooks';
import {
  CalendarRange,
  DatepickerInput,
  DateRange,
} from '@mint-lib/tw-components/patterns';

import { FieldLabel } from './FieldLabel.jsx';

type Props = {
  label?: string;
  infoText?: string;
  startDate: Date | undefined;
  endDate: Date | undefined;
  onSelectCalendarDates: (dateRange?: DateRange) => void;
  onResetCalendar: (dateRange?: DateRange) => void;
};

const RangeDatepicker = ({
  label,
  infoText,
  startDate,
  endDate,
  onSelectCalendarDates,
  onResetCalendar,
}: Props) => {
  const [selectedMonth, setSelectedMonth] = useState<Date | undefined>(
    new Date(),
  );
  const [isOpenDatepicker, setIsOpenDatepicker] = useState(false);
  const [selectedRange, setSelectedRange] = useState<DateRange | undefined>(
    startDate && endDate ? { from: startDate, to: endDate } : undefined,
  );

  const { formattedDateRange } = useDateFormatter(startDate, endDate);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.value) {
      onResetCalendar();
    }
  };

  const handleSelect = (dateRange?: DateRange) => {
    setSelectedRange(dateRange);
  };

  const handleApply = () => {
    onSelectCalendarDates(selectedRange);
    setIsOpenDatepicker(false);
  };

  const handleReset = () => {
    setSelectedRange(undefined);
    onResetCalendar();
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setSelectedRange(
        startDate && endDate ? { from: startDate, to: endDate } : undefined,
      );
    }
    setIsOpenDatepicker(open);
  };

  return (
    <div>
      {label && <FieldLabel label={label} infoText={infoText} />}
      <DatepickerInput
        variant="range"
        align="start"
        value={formattedDateRange}
        open={isOpenDatepicker}
        onOpenChange={handleOpenChange}
        onChangeInput={handleInputChange}
        fullWidth
      >
        <CalendarRange
          selected={{ from: selectedRange?.from, to: selectedRange?.to }}
          onSelect={handleSelect}
          month={selectedMonth}
          disabledDates={{ before: new Date() }}
          onMonthChange={setSelectedMonth}
          onApply={handleApply}
          onReset={handleReset}
        />
      </DatepickerInput>
    </div>
  );
};

export { RangeDatepicker };
